
import React, { useMemo, useState, useEffect } from 'react'
import { CalendarIcon, DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { IconButton, CircularProgress } from '@material-ui/core'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { updateInvoiceDate } from './invoiceApi'
import { updateBillingRecordWithInvoice } from '../../../../../services/billingRecords.service'

const EditableDate = ({ firstDate, setFirstDate, invoiceDetails, invoiceDate, billingRecord, readOnly }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const invoice_id = invoiceDetails?.[0]?._id

  // Compute month boundary based on selected billing month (invoiceDate)
  const { monthStart, monthEnd } = useMemo(() => {
    const base = invoiceDate ? dayjs(invoiceDate) : dayjs()
    return {
      monthStart: base.startOf('month'),
      monthEnd: base.endOf('month'),
    }
  }, [invoiceDate])

  // Ensure current value stays within the selected month
  useEffect(() => {
    if (!firstDate) return
    const current = dayjs(firstDate)
    if (current.isBefore(monthStart, 'day') || current.isAfter(monthEnd, 'day')) {
      // Clamp to month end as per requirement to default to last date
      setFirstDate(monthEnd.format('YYYY-MM-DD'))
    }
  }, [firstDate, monthStart, monthEnd, setFirstDate])

  const handleEditClick = () => {
    // Do not replace state with dayjs object. Keep state as ISO string.
    // DatePicker will receive dayjs(firstDate) as its value.
    setIsEditing(true)
  }

  const handleDateChange = (newValue) => {
    if (!newValue) return
    const picked = dayjs(newValue)
    // Allow only dates within the selected month-year
    if (picked.isBefore(monthStart, 'day') || picked.isAfter(monthEnd, 'day')) {
      return // Ignore out-of-range selections
    }
    setFirstDate(picked.format('YYYY-MM-DD'))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const formatted = dayjs(firstDate).format('YYYY-MM-DD')
      await updateInvoiceDate({ invoice_id, firstDate: formatted })

      //Also update billing record if it exists
      const recordId = billingRecord?.id || billingRecord?._id
      if (billingRecord && recordId) {
        await updateBillingRecordWithInvoice(recordId, {
          invoiceDate: formatted
        })
        console.log('Billing record invoice date updated')
      }
      setFirstDate(formatted)
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  return (
    <div className="flex items-center gap-2">
      {!isEditing ? (
        <>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
            : {dayjs(firstDate).format('DD/MM/YYYY')}
          </span>
          { !readOnly && (
            <IconButton className="p-1" size="small" onClick={handleEditClick}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={firstDate ? dayjs(firstDate) : null}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              // Restrict selection strictly to the selected billing month
              minDate={monthStart}
              maxDate={monthEnd}
              views={["day"]}
              openTo="day"
              slotProps={{
                textField: { size: 'small', className: 'w-32' },
              }}
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
            />
          </LocalizationProvider>
          <div className="flex gap-1 relative">
            <IconButton
              className="p-1"
              size="small"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={16} className="absolute" />
              ) : (
                <SaveIcon className="h-4 w-4" />
              )}
            </IconButton>
            <IconButton
              className="p-1"
              size="small"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <CancelIcon className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditableDate
